/**
 * Fitment confidence tiers (client-side mirror of the backend resolver in
 * routes_public._fitment_confidence) + active-vehicle session persistence.
 *
 * Tiers:
 *  - verified   : same Year + Make + Model (+ Trim when both are known)
 *  - compatible : same Year + Make + Model but a different trim
 *  - likely     : same Make + Model family, different year/fitment
 */
const ACTIVE_VEHICLE_KEY = "lecis_active_vehicle";

export function setActiveVehicle(v) {
  try {
    if (!v) return;
    sessionStorage.setItem(ACTIVE_VEHICLE_KEY, JSON.stringify(v));
  } catch {
    /* sessionStorage unavailable — non-fatal */
  }
}

export function getActiveVehicle() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_VEHICLE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const RANK = { null: 0, likely: 1, compatible: 2, verified: 3 };
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Best confidence tier of `fitments` for a given `vehicle`
 * ({ year, make, model, trim }). Returns null when nothing matches.
 */
export function computeConfidence(fitments, vehicle) {
  if (!vehicle || !Array.isArray(fitments)) return null;
  const vMake = norm(vehicle.make);
  const vModel = norm(vehicle.model);
  const vYear = String(vehicle.year || "");
  const vTrim = norm(vehicle.trim);
  let best = null;
  for (const f of fitments) {
    const fMake = norm(f.make) || norm(f.make_slug);
    const fModel = norm(f.model) || norm(f.model_slug);
    if (fMake !== vMake || fModel !== vModel) continue;
    const fTrim = norm(f.trim);
    let tier;
    if (String(f.year) === vYear) {
      tier = !vTrim || !fTrim || fTrim === vTrim ? "verified" : "compatible";
    } else {
      tier = "likely";
    }
    if (RANK[tier] > RANK[best]) best = tier;
  }
  return best;
}

export const CONFIDENCE_META = {
  verified: {
    label: "Verified Fit",
    icon: "✓",
    cls: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  },
  compatible: {
    label: "Compatible",
    icon: "✓",
    cls: "text-blue-500 border-blue-500/40 bg-blue-500/10",
  },
  likely: {
    label: "Likely Fit",
    icon: "≈",
    cls: "text-amber-500 border-amber-500/40 bg-amber-500/10",
  },
};
